'use client';
import styles from './styles.module.scss';
import colors from '@/theme/colors.module.scss';
import Typography from '@/components/shared/typography/typography.component';
import Image from 'next/image';
import { IhowItWorksComponent } from '@/types';
import { createRef, useEffect, useRef, useState } from 'react';
import { useWindowDimensions } from '@/hook';

export default function HowItWorksSection({
    lang,
    items: howItWorks,
    title,
    subtitle,
}: {
    lang: 'ar' | 'en';
    items: IhowItWorksComponent[];
    title: string;
    subtitle: string;
}) {
    const { IS_DESKTOP, width } = useWindowDimensions();
    const dotsRefs = useRef<any>(
        Array.from({ length: howItWorks.length as number }, a => createRef())
    );
    const imgsRefs = useRef<any>(
        Array.from({ length: howItWorks.length as number }, a => createRef())
    );
    const headerRefs = useRef<any>(
        Array.from({ length: howItWorks.length as number }, a => createRef())
    );
    const [lineHeights, setLineHeights] = useState<number[]>();
    const [dotOffsetValues, setDotOffsetValues] = useState<number[]>([]);

    const calculateLineHeight = () => {
        if (IS_DESKTOP) return;
        if (!dotsRefs.current) return;
        const lineHeightsArr = [];
        for (let i = 0; i < dotsRefs.current.length - 1; i += 1) {
            // console.log(dotsRefs.current[i].current.getBoundingClientRect());
            const { current } = dotsRefs.current[i];
            const { y } = current.getBoundingClientRect();
            const { y: y2 } =
                dotsRefs.current[i + 1].current.getBoundingClientRect();
            const y1_point = y + dotOffsetValues[i];
            const y2_point = y2 + dotOffsetValues[i + 1];
            const dist = Math.abs(y2_point - y1_point);

            lineHeightsArr.push(dist);
            dotsRefs.current[i].current.style.top = `${dotOffsetValues[i]}px`;
        }
        dotsRefs.current[dotsRefs.current.length - 1].current.style.top = `${
            dotOffsetValues[dotsRefs.current.length - 1]
        }px`;
        setLineHeights(lineHeightsArr);
    };
    const calculateDotOffsetvalue = () => {
        if (IS_DESKTOP) return;
        if (!headerRefs.current) return;
        const dotOffsetValuesArr = [];
        for (let i = 0; i < dotsRefs.current.length; i += 1) {
            const { height } =
                headerRefs.current[i].current.getBoundingClientRect();
            dotOffsetValuesArr.push(height);
        }
        setDotOffsetValues(dotOffsetValuesArr);
    };

    useEffect(() => {
        calculateLineHeight();
    }, [dotOffsetValues]);

    useEffect(() => {
        calculateDotOffsetvalue();
    }, [width]);

    return (
        <div className={styles.container} id="how-it-works">
            <div className={styles.badgeContainer}>
                <Typography
                    variant="h3Bold"
                    color={colors.primary}
                    className={` ${styles.title}
          ${styles.animate__animated}  ${styles.animate__pulse}`}
                >
                    {title}
                </Typography>

                <Typography
                    variant="captionMedium"
                    color={colors.secondary}
                    className={`${styles.mobileImage} ${styles.animate__animated}  ${styles.animate__pulse}`}
                >
                    {subtitle}
                </Typography>
            </div>
            <div className={styles.HowItWorksContainerInner}>
                {howItWorks.map((item, index) => {
                    return (
                        <div
                            className={styles.HowItWorksSectionContainer}
                            key={index}
                        >
                            <div
                                ref={dotsRefs?.current[index]}
                                className={
                                    styles.HowItWorksContentSectionDotContainer
                                }
                            >
                                <div
                                    className={
                                        styles.HowItWorksContentSectionDot
                                    }
                                />
                                {lineHeights && (
                                    <div
                                        className={
                                            styles.HowItWorksContentSectionLine
                                        }
                                        style={{
                                            height: `${lineHeights[index]}px`,
                                        }}
                                    />
                                )}
                            </div>

                            <div
                                className={
                                    index % 2
                                        ? styles.HowItWorksSectionItemReverse
                                        : styles.HowItWorksSectionItem
                                }
                            >
                                <div
                                    className={` ${styles.animate__animated} ${
                                        index % 2
                                            ? styles.animate__fadeInRight
                                            : styles.animate__fadeInLeft
                                    }  ${styles.HowItWorksSectionItemImg}
                    ${styles.desktopImage}
                    `}
                                    ref={imgsRefs?.current[index]}
                                >
                                    <Image
                                        src={item.icon}
                                        alt={item.title}
                                        className={styles.imageItem}
                                    />
                                </div>
                                <div
                                    className={` ${
                                        styles.HowItWorksContentSection
                                    }  ${styles.animate__animated} ${
                                        index % 2
                                            ? styles.animate__fadeInLeft
                                            : styles.animate_fadeInRight
                                    }
                  `}
                                    data-wow-delay="0.1s"
                                >
                                    <div
                                        className={
                                            styles.HowItWorksContentSectionNumberContainer
                                        }
                                    >
                                        <Typography
                                            variant="subtitle2Bold"
                                            color={colors.primary}
                                        >
                                            {index + 1}
                                        </Typography>
                                    </div>

                                    <div
                                        className={
                                            styles.HowItWorksContentSectionText
                                        }
                                    >
                                        <div ref={headerRefs?.current[index]}>
                                            <Typography
                                                variant="h5Bold"
                                                className={styles.header}
                                            >
                                                {item.title}
                                            </Typography>
                                        </div>
                                        <Typography
                                            variant="subtitle1"
                                            className={styles.paragraph}
                                        >
                                            {item.description}
                                        </Typography>

                                        <div
                                            ref={imgsRefs?.current[index]}
                                            className={` ${
                                                styles.animate__animated
                                            } ${
                                                index % 2
                                                    ? styles.animate__fadeInRight
                                                    : styles.animate__fadeInLeft
                                            }
                     ${styles.HowItWorksSectionItemImg}
                     ${styles.mobileImage}`}
                                        >
                                            <Image
                                                src={item.icon}
                                                alt={item.title}
                                                className={styles.imageItem}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
